/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useMemo } from 'react';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { Helmet } from 'react-helmet';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Tab from '@mui/material/Tab';
import { useAuth } from 'contexts/AuthContext';
import { TextV2 } from '@insights-ltd/design-library/components';
import Breadcrumbs from 'components/Breadcrumbs';
import { FullScreenError, FullScreenSpinner } from 'components/FullScreen';
import PermissionGuard from 'components/PermissionGuard';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import FullHeight from 'components/FullHeight/FullHeight';
import OrganisationGroupLink from 'components/OrganisationGroupLink/OrganisationGroupLink';
import useTabHistory from 'components/hooks/useTabHistory';
import { DeleteType } from 'pages/Event/types';
import { LocationStateProvider } from 'components/hooks/useHistoryState';
import { parseDurationFromIsoString } from '@insights-ltd/design-library/utils';
import { SpecialHandlingChip } from 'components/SpecialHandlingTag';
import OrganisationInvites from './OrganisationInvites';
import OrganisationPractitioners from './OrganisationPractitioners';
import PractitionerInvitedDialog from './OrganisationInvites/PractitionerInvitedDialog';
import OrganisationEvents from './OrganisationEvents';
import useGetOrganisationData from './useGetOrganisationData';
import { OrganisationViewType } from './typeGuard';

const Organisation: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const { organisationId = '' } = useParams<{ organisationId: string }>();
  const { tabValue: persistedTabValue, persistTab } =
    useTabHistory<OrganisationViewType>();

  const tabValue = state?.tabValue ?? persistedTabValue;
  const defaultTabValue = tabValue ?? 'practitioners';

  const locationState: {
    showInvitedDialog: boolean;
    showInviteDeletedDialog: boolean;
    showDeletedDialog: DeleteType;
    experienceName: string;
    activeTab: OrganisationViewType;
  } = state;

  const removeDialogFromState = () => {
    const { showInvitedDialog, showInviteDeletedDialog, ...rest } =
      locationState;
    navigate(pathname, { replace: true, ...rest });
  };

  const {
    upcomingEvents,
    pastEvents,
    cancelledEvents,
    invites,
    organisation,
    canViewAllOrgs,
    practitioners,
    organisationGroup,
    status,
  } = useGetOrganisationData(organisationId, defaultTabValue, user!);

  const userIsAdminUser = user?.role === 'SUPER_ADMIN';
  const userIsGroupManager = user?.role === 'GROUP_MANAGER';
  const userIsAdvancedUser = user?.role === 'ADVANCED_USER';
  const orgIsInsights = organisationId === 'insights';
  const orgIsSpecialHandling = (organisation?.visibility || '') === 'PRIVATE';
  const orgName = organisation?.name || '';
  const orgId = organisation?.id || '';
  const orgMaximumInactivityPeriod =
    organisation?.dataRetentionPolicy?.maximumInactivityPeriod;
  const orgFormattedRetentionPeriod = orgMaximumInactivityPeriod
    ? parseDurationFromIsoString(orgMaximumInactivityPeriod)
    : null;

  const tabs = useMemo(() => {
    const allTabs = [
      {
        key: 'practitioners',
        label: t('ui.event-management.organisation.practitioners'),
        value: 'practitioners',
      },
      ...(canViewAllOrgs
        ? [
            {
              key: 'invites',
              label: t('ui.event-management.organisation.user-invites'),
              value: 'invites',
            },
          ]
        : []),
      {
        key: 'upcomingEvents',
        label: t('ui.event-management.organisation.active-experience'),
        value: 'upcomingEvents',
      },
      {
        key: 'pastEvents',
        label: t('ui.event-management.organisation.archived-experience'),
        value: 'pastEvents',
      },
      {
        key: 'cancelledEvents',
        label: t('ui.event-management.organisation.cancelled-experience'),
        value: 'cancelled',
      },
    ];

    return allTabs.map((tab) => (
      <Tab key={tab.key} label={tab.label} value={tab.value} />
    ));
  }, [canViewAllOrgs, t]);

  const breadcrumbs: Record<string, string> = {
    '/': t('ui.event-management.dashboard.home.title'),
    '/organisations': t('ui.event-management.organisations.title'),
  };

  if (status === 'pending') {
    return <FullScreenSpinner message={t('ui.event-management.loading')} />;
  }
  if (status === 'error') {
    return (
      <FullScreenError
        message={t('ui.event-management.organisation.error-loading')}
      />
    );
  }

  const showInvitedOrDeletedDialog =
    locationState?.showInvitedDialog || locationState?.showInviteDeletedDialog;

  const editButton = (
    <Grid size={{ xs: 12, md: 'auto' }}>
      <Button
        variant="outlined"
        color="primary"
        to={`/organisations/${organisationId}/edit`}
        component={RouterLink}
        hidden={orgIsInsights}
        aria-label={t('ui.event-management.organisations.button.edit-label')}
      >
        {t('ui.event-management.organisations.button.edit-label')}
      </Button>
    </Grid>
  );

  return (
    <>
      <Helmet>
        <title>
          {t('ui.event-management.title.organisation', { orgName })}
        </title>
      </Helmet>
      <FullHeight>
        <Container
          maxWidth="lg"
          sx={(theme) => ({
            '& > *': {
              marginBottom: theme.spacing(spacingSizeMap.M),
            },
          })}
        >
          <Box
            sx={{
              pt: (theme) => theme.spacing(spacingSizeMap.M),
            }}
          >
            <Breadcrumbs crumbs={breadcrumbs} activeText={orgName} />
          </Box>
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
              '&&': { mb: 0 },
            }}
          >
            <Grid size={{ xs: 12, md: 8 }}>
              <TextV2 variant="h2">
                {`${orgName} `}
                {orgIsSpecialHandling && <SpecialHandlingChip />}
              </TextV2>
            </Grid>
            <PermissionGuard
              requiredPermissions={[
                { action: 'Organisation_Update', resourceId: orgId },
              ]}
            >
              {userIsGroupManager || userIsAdvancedUser
                ? editButton
                : userIsAdminUser && editButton}
            </PermissionGuard>
            {organisationGroup && (
              <Grid
                sx={(theme) => ({
                  margin: `${theme.spacing(spacingSizeMap.S)} 0 ${theme.spacing(
                    spacingSizeMap.XS,
                  )}`,
                })}
                size={{ xs: 12, md: 8 }}
              >
                <OrganisationGroupLink group={organisationGroup} />
              </Grid>
            )}
            {orgFormattedRetentionPeriod && (
              <Grid size={{ xs: 10, md: 8 }}>
                <Trans
                  i18nKey="ui.event-management.organisation.data-retention-period"
                  values={{ years: orgFormattedRetentionPeriod.years }}
                  components={{
                    strong: (
                      <TextV2
                        component="span"
                        variant="bodyBold"
                        color="textSecondary"
                      />
                    ),
                    light: <TextV2 component="span" color="textSecondary" />,
                  }}
                />
              </Grid>
            )}
          </Grid>
          <TabContext value={tabValue ?? 'practitioners'}>
            <TabList
              onChange={(_, value) => {
                persistTab(value);
              }}
              textColor="primary"
              indicatorColor="primary"
            >
              {tabs}
            </TabList>

            <TabPanel
              value="practitioners"
              sx={{
                padding: 0,
              }}
            >
              <OrganisationPractitioners
                practitioners={practitioners || []}
                title={t('ui.event-management.organisation.practitioners')}
                organisationId={orgId}
                organisationGroup={organisationGroup}
              />
            </TabPanel>
            <TabPanel
              value="upcomingEvents"
              sx={{
                padding: 0,
              }}
            >
              <OrganisationEvents
                title={t('ui.event-management.events.upcoming-events')}
                events={upcomingEvents!}
                ascending
              />
            </TabPanel>
            <TabPanel
              value="invites"
              sx={{
                padding: 0,
              }}
            >
              <OrganisationInvites
                invites={invites!}
                title={t('ui.event-management.organisation.user-invites')}
                organisationId={orgId}
              />
            </TabPanel>
            <TabPanel
              value="pastEvents"
              sx={{
                padding: 0,
              }}
            >
              <OrganisationEvents
                title={t('ui.event-management.events.past-events')}
                events={pastEvents!}
              />
            </TabPanel>
            <TabPanel
              value="cancelled"
              sx={{
                padding: 0,
              }}
            >
              <OrganisationEvents
                title={t('ui.event-management.events.cancelled-events')}
                events={cancelledEvents!}
                experienceView={tabValue}
                showDeletedDialog={locationState?.showDeletedDialog}
                deletedExperienceName={locationState?.experienceName}
              />
            </TabPanel>
          </TabContext>
          {showInvitedOrDeletedDialog ? (
            <PractitionerInvitedDialog
              open
              onClose={() => removeDialogFromState()}
              dialogType={
                locationState?.showInvitedDialog ? 'invite' : 'delete'
              }
            />
          ) : null}
        </Container>
      </FullHeight>
    </>
  );
};

const WrappedOrganisation = () => (
  <LocationStateProvider>
    <Organisation />
  </LocationStateProvider>
);

export default WrappedOrganisation;
/* eslint-enable @typescript-eslint/no-non-null-assertion */
